import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug, ContentStatus } from '@jeraisonne/db';
import { useAdminChapterDetail } from '@/lib/admin/queries';
import { useUpdateChapter, slugify } from '@/lib/admin/mutations';
import { IllustrationEditor } from '@/components/illustration-editor';

export function TutorAdminChapterEditPage() {
  const { chapterId } = useParams({ strict: false }) as { chapterId: string };
  const navigate = useNavigate();
  const { data: chapter, isLoading } = useAdminChapterDetail(chapterId);
  const updateChapter = useUpdateChapter();

  const [subject, setSubject] = useState<SubjectSlug>('math');
  const [level, setLevel] = useState<SchoolLevel>('4e');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState(1);
  const [status, setStatus] = useState<ContentStatus>('draft');
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (chapter && !seeded) {
      setSubject(chapter.subject);
      setLevel(chapter.level);
      setTitle(chapter.title);
      setSlug(chapter.slug);
      setDescription(chapter.description ?? '');
      setDisplayOrder(chapter.display_order);
      setStatus(chapter.status);
      setSeeded(true);
    }
  }, [chapter, seeded]);

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugTouched) setSlug(slugify(val));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !slug.trim()) return;
    await updateChapter.mutateAsync({
      id: chapterId,
      subject,
      level,
      title: title.trim(),
      slug: slug.trim(),
      description: description.trim() || null,
      display_order: displayOrder,
      status,
    });
    navigate({ to: '/tutor/admin/chapters/$chapterId', params: { chapterId } });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      {/* Back */}
      <Link
        to="/tutor/admin/chapters/$chapterId"
        params={{ chapterId }}
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {chapter?.title ?? 'Chapitre'}
      </Link>

      <h1 className="font-display text-ink-900 mb-6 text-[22px] font-extrabold">
        Modifier le chapitre
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Matière</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value as SubjectSlug)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Niveau</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as SchoolLevel)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              {SCHOOL_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Slug <span className="text-ink-400 ml-1.5 font-normal">(URL)</span>
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-mono text-[14px] focus:outline-none focus:ring-2"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Description <span className="text-ink-400 ml-1.5 font-normal">(optionnel)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 resize-none rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Ordre</label>
            <input
              type="number"
              min={1}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 1)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Statut</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ContentStatus)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
              <option value="archived">Archivé</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Link to="/tutor/admin/chapters/$chapterId" params={{ chapterId }} className="flex-1">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button
            type="submit"
            className="flex-1"
            disabled={updateChapter.isPending || !title.trim() || !slug.trim()}
          >
            {updateChapter.isPending ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>

        {updateChapter.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{updateChapter.error?.message}</p>
        )}
      </form>

      <div className="mt-6">
        <IllustrationEditor
          entityType="chapter"
          entityId={chapterId}
          initialUrl={chapter?.illustration_url}
          initialPrompt={chapter?.illustration_prompt}
          subject={subject}
          level={level}
          title={title}
        />
      </div>
    </div>
  );
}
